<script setup lang="ts">
import {PhArrowRight} from "@phosphor-icons/vue";
import type {
  Component,
  FocalPointAsset,
  FromResolver,
} from "#magnolia-layer/types/magnolia-types";
import type {LinkTypes} from "#magnolia-layer/types/api";

const props = defineProps<{
  metadata: Component;
  title: string;
  eyebrow: string;
  link?: LinkTypes;
  image?: FromResolver<FocalPointAsset>;
  imageOverlay?: string;
}>();

const link = props.link ? resolveLink(props) : undefined;

const overlayClass = computed(() => {
  const key = props.imageOverlay ?? "none";
  return (
    {
      vignette: "vignette",
      "stripe-left": "logo-overlay logo-overlay-left",
      "stripe-right": "logo-overlay logo-overlay-right",
    }[key] ?? undefined
  );
});
</script>

<template>
  <article
    class="text-white h-full overflow-hidden content-grid content-grid-force">
    <div
      class="relative z-10 h-full pt-8 pb-16 xl:py-16 swiper-transition-target flex flex-col justify-between lg:inner-content-grid">
      <span class="col-start-1 col-span-4 text-xl drop-shadow-lg">{{
        eyebrow
      }}</span>
      <div class="row-start-2 self-end col-start-7 col-span-full">
        <h1 class="text-2xl uppercase drop-shadow-lg">
          {{ title }}
        </h1>
        <NuxtLinkLocale
          v-if="link && link.link"
          :href="link.link"
          :target="link.target"
          class="link-with-icon link-underline mt-8">
          {{ $t("label.continueReading") }}
          <PhArrowRight></PhArrowRight>
        </NuxtLinkLocale>
      </div>
    </div>
    <MagnoliaPicture
      v-if="image"
      :modifiers="{
        grayscale: 1,
      }"
      class="absolute inset-0 flex items-center justify-center"
      :asset="image"></MagnoliaPicture>
    <div
      class="absolute inset-0 pointer-events-none"
      :class="overlayClass"></div>
  </article>
</template>

<style scoped lang="scss">
.vignette {
  background: radial-gradient(
      42.96% 94.96% at 62.41% 35.15%,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.9) 100%
    )
    /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */;
}

.swiper-transition-target {
  opacity: 0;
  transition: all 0.5s 0.5s ease;
}

.swiper-slide-active .swiper-transition-target {
  opacity: 1;
}

picture {
  :deep(img) {
    display: block;
    object-position: center;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
